import { useState } from 'react';

export default function Donation() {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    
    // フォームデータを変換
    for (const [key, value] of formData.entries()) {
      data.append(key, value.toString());
    }
    
    try {
      const response = await fetch('https://readdy.ai/api/forms/donation/a1b2c3d4', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data,
      });
      
      if (response.ok) {
        setShowThankYou(true);
        setTimeout(() => setShowThankYou(false), 3000);
        e.currentTarget.reset();
        setSelectedPlan('');
        setDonationAmount('');
        setCustomAmount('');
      }
    } catch (error) {
      console.error('送信エラー:', error);
    }
  };

  const sponsorshipPlans = [
    {
      id: 'premium',
      title: '創設パートナー',
      amount: '4,500,000円',
      period: '6ヶ月',
      benefits: [
        '福祉車両サイド・後方広告掲載（6ヶ月）',
        '事業所看板への企業名永年掲載\n※開業前支援企業様だけの特典\n※格上表記',
        '事業所案内パンフレットへの企業名掲載（2年）',
        '開業記念式典でのご挨拶機会',
        '定期活動報告書の送付'
      ]
    },
    {
      id: 'standard',
      title: '協賛サポーター',
      amount: '3,000,000円',
      period: '3ヶ月',
      benefits: [
        '福祉車両サイド・後方広告掲載（3ヶ月）',
        '事業所看板への企業名永年掲載\n※開業前支援企業様だけの特典',
        '事業所案内パンフレットへの企業名掲載（1年）',
        '定期活動報告書の送付'
      ]
    }
  ];

  const quickAmounts = ['10000', '30000', '50000', '100000', '300000', '500000'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-rose-50">
      {/* Hero Section */}
      <div className="relative text-white py-20" style={{ background: 'linear-gradient(135deg, #ffa7cd 0%, #ffb347 50%, #ff8fab 100%)' }}>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c154f61a865add57c3f80887f798bf99.jpg')`
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left md:flex-1">
              <div className="flex justify-center md:justify-start items-center mb-6">
                <img 
                  src="https://static.readdy.ai/image/d740e2b34c55af2f7b572916836ca234/e12563d933ea14c3e7a230f8f632e751.png" 
                  alt="てとて ロゴ" 
                  className="w-28 h-28 mr-6"
                />
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">
                    重度心身障害児のための
                    <br />
                    放課後等デイサービス開設支援
                  </h1>
                </div>
              </div>
              <p className="text-lg md:text-xl mb-6 opacity-90">
                待っている子どもたちと家族のために、今すぐ始められるサービスを
              </p>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mt-6 md:mt-0 md:ml-12">
              <div className="flex justify-center space-x-8 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold">50+</div>
                  <div>待機中の家族</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">2-3年</div>
                  <div>融資待機期間</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">24/7</div>
                  <div>サポート体制</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* 緊急性の説明セクション */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 border-l-4 p-6 mb-8" style={{ borderLeftColor: '#ffb347' }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#d63384' }}>
              <i className="ri-alarm-warning-line mr-2"></i>
              緊急性の高い現状
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: '#d63384' }}>
              現在、重度心身障害児を持つ多くの家族が放課後等デイサービスの開設を切実に待ち望んでいます。
              融資による資金調達には2〜3年の期間を要しますが、子どもたちの成長は待ってくれません。
              クラウドファンディングによる迅速な資金調達で、一日でも早くサービスを開始したいと考えています。
            </p>
          </div>
        </div>

        {/* 企業スポンサープラン */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
            企業スポンサープラン
          </h2>
          
          {/* 従来の広告との比較セクション */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12 border-t-4" style={{ borderTopColor: '#4f46e5' }}>
            <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
              <i className="ri-bar-chart-line mr-3"></i>
              従来の車両ラッピング広告との比較
            </h3>
            
            {/* 車両運行と価格根拠の説明 */}
            <div className="bg-white/80 rounded-xl p-6 mb-8 border-l-4" style={{ borderLeftColor: '#4f46e5' }}>
              <h4 className="text-lg font-bold mb-4 text-gray-800">
                <i className="ri-car-line mr-2"></i>
                運行計画と価格設定根拠
              </h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold mb-3 text-indigo-700">
                    <i className="ri-checkbox-circle-line mr-2 text-indigo-600"></i>
                    <span><strong>3台の福祉車両</strong>で名古屋市内を運行</span>
                  </h5>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-line mr-2 text-indigo-600"></i>
                      <span>緑区を中心とした<strong>広域送迎エリア</strong></span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-line mr-2 text-indigo-600"></i>
                      <span>平日毎日の<strong>定期運行ルート</strong></span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-line mr-2 text-indigo-600"></i>
                      <span>医療機関・学校・イベント会場への<strong>特別運行</strong></span>
                    </div>
                  </div>
                </div>
                <div>
                  <h5 className="font-semibold mb-3 text-indigo-700">
                    <i className="ri-calculator-line mr-2"></i>
                    価格算出根拠
                  </h5>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center">
                      <i className="ri-arrow-right-s-line mr-2 text-indigo-600"></i>
                      <span><strong>3台運行 → 1台あたり月額100-150万円</strong></span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-right-s-line mr-2 text-indigo-600"></i>
                      <span>一般車両ラッピング：<strong>月額80-120万円</strong></span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-right-s-line mr-2 text-indigo-600"></i>
                      <span>福祉車両の<strong>高い注目度・記憶定着率</strong></span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-right-s-line mr-2 text-indigo-600"></i>
                      <span><strong>社会貢献価値</strong>による付加価値</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: '#4f46e5' }}>
                    <th className="py-4 px-6 text-left font-bold text-gray-800">項目</th>
                    <th className="py-4 px-6 text-center font-bold text-white rounded-t-lg" style={{ backgroundColor: '#ff8fab' }}>
                      福祉車両スポンサー<br />
                      <span className="text-xs font-normal opacity-90">（てとて・3台運行）</span>
                    </th>
                    <th className="py-4 px-6 text-center font-bold text-gray-600">
                      一般車両ラッピング<br />
                      <span className="text-xs font-normal">（従来型・1台）</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">月額費用</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>100-150万円</span><br />
                      <span className="text-xs">※3台運行・1台当たり</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-700">80-120万円</span><br />
                      <span className="text-xs text-gray-500">※1台運行</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">広告掲載期間</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>3〜6ヶ月</span>
                    </td>
                    <td className="py-4 px-6 text-center">1〜3ヶ月</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">運行範囲</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>名古屋市内広域</span><br />
                      <span className="text-xs">緑区中心・3台同時運行</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-700">限定エリア</span><br />
                      <span className="text-xs text-gray-500">1台のみ</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">社会貢献度</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>★★★★★</span><br />
                      <span className="text-xs">障害児支援・地域福祉</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-500">★★☆☆☆</span><br />
                      <span className="text-xs text-gray-500">商業宣伝のみ</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">追加特典</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>あり</span><br />
                      <span className="text-xs">施設パンフレット企業名掲載・創業前支援にて施設看板企業名永年掲載・報告書</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-500">なし</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">ブランド価値</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>向上</span><br />
                      <span className="text-xs">CSR・SDGs貢献</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-500">変化なし</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-semibold text-gray-700">記録・報告</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>詳細報告</span><br />
                      <span className="text-xs">活動レポート定期送付</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-500">基本報告のみ</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-semibold text-gray-700">税制上の取扱い</td>
                    <td className="py-4 px-6 text-center bg-pink-50">
                      <span className="font-semibold" style={{ color: '#ff8fab' }}>寄付金控除対象</span><br />
                      <span className="text-xs">（一般社団法人認定後）</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-gray-500">広告宣伝費</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                ※ 3台の福祉車両が名古屋市内を運行することで、1台あたりのコストパフォーマンスが向上し、より多くの方々への露出が可能になります
              </p>
            </div>
          </div>

          {/* 支援企業様への詳細メリット */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 mb-12 border-t-4" style={{ borderTopColor: '#059669' }}>
            <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
              <i className="ri-trophy-line mr-3"></i>
              支援企業様への詳細メリット
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold mb-4 text-emerald-700">
                  <i className="ri-medal-line mr-2"></i>
                  ブランド価値向上
                </h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>CSR活動の具体的な成果として対外的にアピール可能</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>SDGs目標3「すべての人に健康と福祉を」への直接貢献</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>地域密着企業としての信頼性向上</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>社員のモチベーション向上と採用力強化</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-bold mb-4 text-emerald-700">
                  <i className="ri-line-chart-line mr-2"></i>
                  マーケティング効果
                </h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>福祉車両の特殊性により高い注目度・記憶に残りやすい</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>地域コミュニティでの口コミ効果</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>SNSでの拡散による無料PR効果</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line mt-1 mr-2 text-emerald-600"></i>
                    <span>メディア取材による露出機会の増加</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 実際の広告効果データ */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12 border-t-4" style={{ borderTopColor: '#8b5cf6' }}>
            <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
              <i className="ri-pie-chart-line mr-3"></i>
              期待される広告効果
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center bg-purple-50 rounded-xl p-6">
                <div className="text-3xl font-bold mb-2 text-purple-700">1,500+</div>
                <h4 className="font-semibold mb-2 text-gray-800">月間接触者数</h4>
                <p className="text-xs text-gray-600">
                  送迎ルート・駐車場・イベント参加による推定接触者数
                </p>
              </div>
              
              <div className="text-center bg-indigo-50 rounded-xl p-6">
                <div className="text-3xl font-bold mb-2 text-indigo-700">85%</div>
                <h4 className="font-semibold mb-2 text-gray-800">記憶定着率</h4>
                <p className="text-xs text-gray-600">
                  福祉車両への企業ロゴは通常車両より記憶に残りやすい
                </p>
              </div>
              
              <div className="text-center bg-blue-50 rounded-xl p-6">
                <div className="text-3xl font-bold mb-2 text-blue-700">3-6ヶ月</div>
                <h4 className="font-semibold mb-2 text-gray-800">継続露出期間</h4>
                <p className="text-xs text-gray-600">
                  長期間の継続的なブランド露出による認知度向上
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {sponsorshipPlans.map((plan) => (
              <div 
                key={plan.id}
                className={`bg-white rounded-2xl shadow-lg p-8 border-2 transition-all duration-300 hover:shadow-xl ${
                  selectedPlan === plan.id ? 'bg-gradient-to-br from-pink-50 to-orange-50' : 'border-gray-200'
                }`}
                style={{ 
                  borderColor: selectedPlan === plan.id ? '#ffb347' : undefined,
                  borderTopWidth: '4px',
                  borderTopColor: plan.id === 'premium' ? '#ff8fab' : '#ffb347'
                }}
              >
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.title}</h3>
                  <div className="text-4xl font-bold mb-2" style={{ color: plan.id === 'premium' ? '#ff8fab' : '#ffb347' }}>{plan.amount}</div>
                  <div className="text-gray-600">広告掲載期間: {plan.period}</div>
                </div>
                
                <div className="space-y-3 mb-6">
                  {plan.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <i className="ri-check-line mt-1 mr-3 flex-shrink-0" style={{ color: '#ffb347' }}></i>
                      <span className="text-gray-700 whitespace-pre-line">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                    selectedPlan === plan.id
                      ? 'text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={{ 
                    backgroundColor: selectedPlan === plan.id ? (plan.id === 'premium' ? '#ff8fab' : '#ffb347') : undefined
                  }}
                >
                  このプランを選択
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* 一般寄付セクション */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
            一般寄付
          </h2>
          <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4" style={{ borderTopColor: '#ffb347' }}>
            <p className="text-center text-gray-600 mb-8 text-lg">
              どのような金額でもお気持ちをお聞かせください。皆様のご支援が子どもたちの笑顔につながります。
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => {
                    setDonationAmount(amount);
                    setCustomAmount('');
                  }}
                  className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all duration-300 whitespace-nowrap ${
                    donationAmount === amount
                      ? 'bg-gradient-to-r from-orange-50 to-pink-50 shadow-md'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{
                    borderColor: donationAmount === amount ? '#ffb347' : undefined,
                    color: donationAmount === amount ? '#ff8fab' : undefined
                  }}
                >
                  {parseInt(amount).toLocaleString()}円
                </button>
              ))}
            </div>
            
            <div className="text-center">
              <input
                type="number"
                placeholder="その他の金額を入力"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setDonationAmount('');
                }}
                className="w-full max-w-md px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none text-center text-lg"
                style={{ 
                  borderColor: customAmount ? '#ffb347' : undefined,
                  focusBorderColor: '#ffb347'
                }}
              />
            </div>
          </div>
        </div>

        {/* 寄付フォーム */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4" style={{ borderTopColor: '#ffb347' }}>
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
            寄付・スポンサー申込みフォーム
          </h2>
          
          {showThankYou && (
            <div className="bg-gradient-to-r from-orange-50 to-pink-50 border-l-4 p-4 mb-6" style={{ borderLeftColor: '#ffb347' }}>
              <div className="flex items-center">
                <i className="ri-check-circle-line text-xl mr-3" style={{ color: '#ffb347' }}></i>
                <p className="font-semibold" style={{ color: '#d63384' }}>
                  お申込みありがとうございます。後日詳細をご連絡いたします。
                </p>
              </div>
            </div>
          )}
          
          <form id="donation-form" onSubmit={handleSubmit} data-readdy-form>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  お名前 / 企業名 *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none text-sm focus:border-orange-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  メールアドレス *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none text-sm focus:border-orange-300"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none text-sm focus:border-orange-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ご住所
                </label>
                <input
                  type="text"
                  name="address"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none text-sm focus:border-orange-300"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                支援タイプ *
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="support_type"
                    value="premium_sponsor"
                    checked={selectedPlan === 'premium'}
                    onChange={() => setSelectedPlan('premium')}
                    className="mr-3"
                    style={{ accentColor: '#ff8fab' }}
                  />
                  <span className="text-sm">創設パートナー（4,500,000円）</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="support_type"
                    value="standard_sponsor"
                    checked={selectedPlan === 'standard'}
                    onChange={() => setSelectedPlan('standard')}
                    className="mr-3"
                    style={{ accentColor: '#ffb347' }}
                  />
                  <span className="text-sm">協賛サポーター（3,000,000円）</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="support_type"
                    value="general_donation"
                    checked={selectedPlan === 'general'}
                    onChange={() => setSelectedPlan('general')}
                    className="mr-3"
                    style={{ accentColor: '#ffb347' }}
                  />
                  <span className="text-sm">一般寄付</span>
                </label>
              </div>
            </div>
            
            {selectedPlan === 'general' && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  寄付金額 *
                </label>
                <input
                  type="hidden"
                  name="donation_amount"
                  value={donationAmount || customAmount}
                />
                <div className="text-lg font-semibold" style={{ color: '#ff8fab' }}>
                  {(donationAmount || customAmount) && 
                    `${parseInt(donationAmount || customAmount).toLocaleString()}円`
                  }
                </div>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                応援メッセージ・ご要望
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none resize-none text-sm focus:border-orange-300"
                placeholder="子どもたちや事業への応援メッセージをお聞かせください（500文字以内）"
              ></textarea>
            </div>
            
            <div className="text-center">
              <button
                type="submit"
                className="text-white px-12 py-4 rounded-lg font-bold text-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap shadow-lg"
                style={{ background: 'linear-gradient(135deg, #ffa7cd 0%, #ffb347 50%, #ff8fab 100%)' }}
              >
                <i className="ri-heart-line mr-2"></i>
                寄付・スポンサー申込み
              </button>
            </div>
          </form>
        </div>

        {/* 活用方法セクション */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
            ご支援の活用方法
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-t-4" style={{ borderTopColor: '#ffb347' }}>
              <div 
                className="w-16 h-16 mx-auto mb-4 bg-cover bg-center rounded-full"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20accessible%20therapy%20room%20with%20medical%20equipment%20for%20disabled%20children%2C%20bright%20and%20clean%20interior%2C%20therapeutic%20tools%20and%20wheelchairs%2C%20warm%20pink%20and%20orange%20accent%20lighting&width=200&height=200&seq=facility-warm&orientation=squarish')`
                }}
              ></div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">施設整備</h3>
              <p className="text-gray-600 text-sm">
                木の温もりあふれる安心できる「Home」のような施設で、バリアフリー環境と専門機器を整備し、最適なケア環境を構築します
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-t-4" style={{ borderTopColor: '#ffa7cd' }}>
              <div 
                className="w-16 h-16 mx-auto mb-4 bg-cover bg-center rounded-full"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20healthcare%20staff%20team%20caring%20for%20disabled%20children%2C%20diverse%20therapists%20and%20nurses%2C%20warm%20atmosphere%2C%20soft%20pink%20and%20orange%20background&width=200&height=200&seq=staff-warm&orientation=squarish')`
                }}
              ></div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">専門スタッフ</h3>
              <p className="text-gray-600 text-sm">
                重心型放課後等デイサービスでは児童相談管理者、保育士、看護師、理学療法士・作業療法士、ケアワーカーが連携し、質の高いサービスを提供します
              </p>
            </div>
          </div>
        </div>

        {/* お問い合わせ */}
        <div className="mt-16 text-center bg-gradient-to-r from-pink-50 via-orange-50 to-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">お問い合わせ</h2>
          <p className="text-gray-600 mb-6">
            ご不明な点やご相談がございましたら、お気軽にお問い合わせください<br />
            （仮）Wa－international　代表　青木香奈枝
          </p>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <div className="flex items-center">
              <i className="ri-phone-line text-xl mr-2" style={{ color: '#ffb347' }}></i>
              <span>090-3253-4080</span>
            </div>
            <div className="flex items-center">
              <i className="ri-mail-line text-xl mr-2" style={{ color: '#ff8fab' }}></i>
              <span>kanaea48@gmail.com</span>
            </div>
            <div className="flex items-center">
              <i className="ri-instagram-line text-xl mr-2" style={{ color: '#d63384' }}></i>
              <span>@tetote_wainternational</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
